'use client';

import {
  useActionState,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { useFormStatus } from 'react-dom';
import { submitLead } from '@/app/[lang]/aloqa/actions';
import {
  ARRIVAL_EVENT,
  consumeArrivalFlag,
} from '@/components/transition/OrderTransition';
import { Icon } from '@/components/ui/Icon';
import { primaryPhone } from '@/data/site';
import type { Locale } from '@/i18n/config';
import type { Dictionary } from '@/i18n/types';
import { cn } from '@/lib/cn';
import { initialLeadState } from '@/lib/lead';

/**
 * The form's own strings, narrowed from the dictionary by the page that renders
 * it. A Client Component cannot read `next/root-params`, so copy arrives as
 * props — and only the parts this component actually renders, so the sales
 * notification wording never crosses into the browser payload.
 */
export type ContactFormCopy = {
  formTitle: string;
  formLead: string;
  consent: string;
  fields: Dictionary['contact']['fields'];
  submit: string;
  submitting: string;
  success: Dictionary['contact']['success'];
};

const fieldBase =
  'w-full rounded-xl border bg-white px-4 text-[0.9375rem] text-ink placeholder:text-muted/70 ' +
  'transition-colors duration-200 focus:border-brand-500 focus:outline-none ' +
  'focus:ring-4 focus:ring-brand-500/15';

function Label({
  htmlFor,
  children,
  required,
}: {
  htmlFor: string;
  children: string;
  required?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-[0.875rem] font-bold text-ink"
    >
      {children}
      {required ? (
        <span className="text-brand-600" aria-hidden="true">
          {' '}
          *
        </span>
      ) : null}
    </label>
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-2 text-[0.8125rem] font-semibold text-[#c2374a]">
      {message}
    </p>
  );
}

function SubmitButton({ idle, busy }: { idle: string; busy: string }) {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className={cn(
        'group/btn inline-flex h-14 items-center justify-center gap-2.5 rounded-full',
        'bg-brand-600 px-8 text-base font-bold text-white',
        'shadow-[0_10px_24px_-10px_rgb(22_104_224/0.7)]',
        'transition-[background-color,box-shadow,opacity] duration-200',
        'hover:bg-brand-700 disabled:cursor-progress disabled:opacity-70',
      )}
    >
      {pending ? busy : idle}
      <Icon
        name={pending ? 'clock' : 'send'}
        size={18}
        className="transition-transform duration-200 group-hover/btn:translate-x-0.5 motion-reduce:transition-none"
      />
    </button>
  );
}

/**
 * Shown instead of the form after a delivered lead. The card spins in
 * slowly out of a blur (`success-spin-in`), then the ring and check draw
 * themselves and the copy surfaces line by line — timings in globals.css.
 */
function SuccessCard({
  copy,
  onReset,
}: {
  copy: ContactFormCopy['success'];
  onReset: () => void;
}) {
  return (
    <div className="success-card relative isolate flex min-h-[30rem] flex-col items-center justify-center overflow-hidden rounded-2xl bg-navy-900 px-7 py-14 text-center sm:px-12">
      <div aria-hidden="true" className="absolute inset-0 grid-bg" />
      <div
        aria-hidden="true"
        className="absolute -top-24 left-1/2 -z-10 size-[26rem] -translate-x-1/2 rounded-full bg-brand-600/25 blur-[100px]"
      />

      <div className="relative grid size-28 place-items-center">
        <span
          aria-hidden="true"
          className="success-halo absolute inset-0 rounded-full border border-brand-400/60"
        />
        <span
          aria-hidden="true"
          className="success-orbit absolute -inset-2 rounded-full border border-dashed border-white/12"
        />
        <svg viewBox="0 0 100 100" className="size-24" aria-hidden="true">
          <defs>
            <linearGradient id="success-ring-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="var(--color-brand-400)" />
              <stop offset="1" stopColor="var(--color-brand-300)" />
            </linearGradient>
          </defs>
          <circle
            className="success-ring"
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="url(#success-ring-grad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
          />
          <path
            className="success-check"
            d="M32 51.5 45 64.5 69 38"
            fill="none"
            stroke="#ffffff"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <p data-reveal="1" className="label-xs mt-9 text-brand-300">
        {copy.eyebrow}
      </p>
      <h3
        data-reveal="2"
        className="mt-3 text-[clamp(1.5rem,1.25rem+0.9vw,1.875rem)] text-white"
      >
        {copy.title}
      </h3>
      <p
        data-reveal="3"
        className="mt-4 max-w-md text-[0.9375rem] leading-relaxed text-ondark"
      >
        {copy.lead}
      </p>

      <div
        data-reveal="4"
        className="mt-9 flex flex-col items-center gap-4 sm:flex-row sm:gap-6"
      >
        <a
          href={primaryPhone.href}
          className="inline-flex h-12 items-center gap-2.5 rounded-full border border-white/15 bg-white/8 px-6 text-[0.9375rem] font-bold text-white backdrop-blur-sm transition-colors duration-200 hover:border-white/30 hover:bg-white/14"
        >
          <Icon name="phone" size={17} className="text-brand-300" />
          {primaryPhone.value}
        </a>
        <button
          type="button"
          onClick={onReset}
          className="text-[0.875rem] font-semibold text-ondark-muted underline decoration-white/25 underline-offset-4 transition-colors hover:text-white"
        >
          {copy.again}
        </button>
      </div>
    </div>
  );
}

function LeadForm({
  lang,
  topics,
  copy,
  nameRef,
  onReset,
}: {
  lang: Locale;
  topics: readonly string[];
  copy: ContactFormCopy;
  nameRef: RefObject<HTMLInputElement | null>;
  onReset: () => void;
}) {
  const [state, formAction] = useActionState(submitLead, initialLeadState);
  const uid = useId();
  const { fields } = copy;

  const ids = {
    name: `${uid}-name`,
    phone: `${uid}-phone`,
    topic: `${uid}-topic`,
    brief: `${uid}-brief`,
  };

  return (
    <>
      {state.status === 'success' ? (
        <SuccessCard copy={copy.success} onReset={onReset} />
      ) : (
        <div className="rounded-2xl border border-line bg-white p-7 shadow-(--shadow-card) sm:p-9 lg:p-10">
          <h2
            data-stagger="1"
            className="text-[clamp(1.375rem,1.1rem+0.9vw,1.75rem)]"
          >
            {copy.formTitle}
          </h2>
          <p
            data-stagger="2"
            className="mt-3 text-[0.9375rem] leading-relaxed text-body"
          >
            {copy.formLead}
          </p>

          {/* `relative` keeps the absolutely positioned honeypot below anchored
              here instead of at the document level. */}
          <form action={formAction} className="relative mt-8" noValidate>
            {/* The action cannot read root params, so the language travels with
                the submission — it decides which dictionary validates it and
                which language the sales notification is written in. */}
            <input type="hidden" name="locale" value={lang} />

            {/* Honeypot — hidden from users and from assistive tech. */}
            <div aria-hidden="true" className="absolute h-0 w-0 overflow-hidden">
              <label htmlFor={`${uid}-hp`}>{fields.honeypot}</label>
              <input
                id={`${uid}-hp`}
                type="text"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div data-stagger="3" className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor={ids.name} required>
                  {fields.name}
                </Label>
                <input
                  ref={nameRef}
                  id={ids.name}
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder={fields.namePlaceholder}
                  aria-describedby={
                    state.errors.name ? `${ids.name}-err` : undefined
                  }
                  aria-invalid={state.errors.name ? true : undefined}
                  className={cn(
                    fieldBase,
                    'h-13',
                    state.errors.name ? 'border-[#c2374a]' : 'border-line',
                  )}
                />
                <FieldError id={`${ids.name}-err`} message={state.errors.name} />
              </div>

              <div>
                <Label htmlFor={ids.phone} required>
                  {fields.phone}
                </Label>
                <input
                  id={ids.phone}
                  name="phone"
                  type="tel"
                  required
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={fields.phonePlaceholder}
                  aria-describedby={
                    state.errors.phone ? `${ids.phone}-err` : undefined
                  }
                  aria-invalid={state.errors.phone ? true : undefined}
                  className={cn(
                    fieldBase,
                    'h-13',
                    state.errors.phone ? 'border-[#c2374a]' : 'border-line',
                  )}
                />
                <FieldError
                  id={`${ids.phone}-err`}
                  message={state.errors.phone}
                />
              </div>
            </div>

            <div data-stagger="4" className="mt-5">
              <Label htmlFor={ids.topic}>{fields.topic}</Label>
              <div className="relative">
                <select
                  id={ids.topic}
                  name="topic"
                  defaultValue=""
                  className={cn(
                    fieldBase,
                    'h-13 appearance-none border-line pr-12',
                  )}
                >
                  <option value="">{fields.topicPlaceholder}</option>
                  {topics.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
                <Icon
                  name="chevronDown"
                  size={18}
                  className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-muted"
                />
              </div>
              <FieldError id={`${ids.topic}-err`} message={state.errors.topic} />
            </div>

            <div data-stagger="5" className="mt-5">
              <Label htmlFor={ids.brief} required>
                {fields.brief}
              </Label>
              <textarea
                id={ids.brief}
                name="brief"
                required
                rows={8}
                placeholder={fields.briefPlaceholder}
                aria-describedby={
                  state.errors.brief ? `${ids.brief}-err` : undefined
                }
                aria-invalid={state.errors.brief ? true : undefined}
                className={cn(
                  fieldBase,
                  'resize-y py-3.5 leading-relaxed',
                  state.errors.brief ? 'border-[#c2374a]' : 'border-line',
                )}
              />
              <FieldError id={`${ids.brief}-err`} message={state.errors.brief} />
            </div>

            <div data-stagger="6" className="mt-8">
              <SubmitButton idle={copy.submit} busy={copy.submitting} />
            </div>

            <p
              data-stagger="7"
              className={cn(
                'mt-5 text-[0.875rem] leading-relaxed',
                state.status === 'error'
                  ? 'font-semibold text-[#c2374a]'
                  : 'text-muted',
              )}
            >
              {state.status === 'error' ? state.message : copy.consent}
            </p>
          </form>
        </div>
      )}

      {/* One persistent region announces the outcome to screen readers —
          the visual result is either the inline banner or the success card. */}
      <p aria-live="polite" className="sr-only">
        {state.status === 'idle' ? '' : state.message}
      </p>
    </>
  );
}

export function ContactForm({
  lang,
  topics,
  copy,
}: {
  lang: Locale;
  topics: readonly string[];
  copy: ContactFormCopy;
}) {
  /*
   * Arrival choreography: plays only when the visitor lands here through an
   * order CTA (session flag across the route change, or a window event when
   * already on the page) — never on a cold page load.
   */
  const [arriving, setArriving] = useState(false);
  const [formPass, setFormPass] = useState(0);
  const nameRef = useRef<HTMLInputElement>(null);

  // Class off → double rAF → on, so a repeat click restarts the CSS
  // animations without remounting the form (typed input survives).
  const arrive = useCallback(() => {
    setArriving(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setArriving(true));
    });
  }, []);

  useEffect(() => {
    window.addEventListener(ARRIVAL_EVENT, arrive);
    // The flag is written just before the route change. Reading it in a frame
    // callback keeps the effect body free of a synchronous state update, and
    // the curtain is still covering the page for ~240ms either way.
    const frame = requestAnimationFrame(() => {
      if (consumeArrivalFlag()) arrive();
    });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener(ARRIVAL_EVENT, arrive);
    };
  }, [arrive]);

  // Hand the keyboard over once the card has settled under the lifted curtain.
  useEffect(() => {
    if (!arriving) return;
    const timer = window.setTimeout(
      () => nameRef.current?.focus({ preventScroll: true }),
      950,
    );
    return () => window.clearTimeout(timer);
  }, [arriving]);

  return (
    <div className={cn('rounded-2xl', arriving && 'ariza-arrive')}>
      <LeadForm
        key={formPass}
        lang={lang}
        topics={topics}
        copy={copy}
        nameRef={nameRef}
        onReset={() => setFormPass((pass) => pass + 1)}
      />
    </div>
  );
}
